import React, {useEffect, useState} from 'react';
import {Col, DatePicker, Form, Modal, Row, Select} from 'antd';
import "../../assets/css/Dentobook.css";
import "../../assets/css/PatientPhotos.css";
import {FormattedMessage, useIntl} from "react-intl";
import {CaretDownOutlined, CloseOutlined} from '@ant-design/icons';
import {isMobileOnly} from "react-device-detect";
import {
    InvoiceResponse,
    Organization,
    Patient,
    ProformaInvoiceUpdate
} from "../../api-client";
import {InvoicePaymentMethodEnum, InvoiceStatusEnum} from "../../types/calendarEnums";
import {AxiosResponse} from "axios";
import dayjs, {Dayjs} from "dayjs";
import utc from 'dayjs/plugin/utc';
import {getOrganizationApi, getInvoicesApi} from "../../api/apiInstances";
import logger from "../../utils/logger";

interface PatientProformaCreateModalProps {
    patient: Patient;
    isModalOpen: boolean;
    setIsModalOpen: Function;
    proforma: InvoiceResponse;
}

dayjs.extend(utc);

const PatientProformaCreateModal = ({
                                        isModalOpen, setIsModalOpen, patient, proforma
                                    }: PatientProformaCreateModalProps) => {

    const intl = useIntl();
    const [date, setDate] = useState<Dayjs | undefined>();
    const [paymentDeadline, setPaymentDeadline] = useState<dayjs.Dayjs | null>(null);
    const [paymentMethod, setPaymentMethod] = useState<InvoicePaymentMethodEnum>();
    const [vat, setVat] = useState<string>();
    const [loading, setLoading] = useState<boolean>(false);

    const organizationApi = getOrganizationApi();
    const invoicesApi = getInvoicesApi();

    useEffect(() => {
    }, [isModalOpen]);

    useEffect(() => {
        if (proforma.paymentDueDate) {
            const formattedDate = dayjs(proforma.paymentDueDate).utc();
            if (formattedDate.isValid()) {
                setPaymentDeadline(formattedDate);
            }
        }
        if (proforma.date) {
            const formattedDate = dayjs(proforma.date).utc();
            if (formattedDate.isValid()) {
                setDate(formattedDate);
            }
        }
    }, [proforma.paymentDueDate, proforma.date]);

    const handleCancel = () => {
        setIsModalOpen(false);
    }

    const handleOk = async () => {
        setLoading(true);

        try {
            const organization: AxiosResponse<Organization> = await organizationApi.fetchOrganizationById(patient.organizationId!);

            // Calculate VAT
            let vatRate = 0.20;
            if (typeof vat === "string") {
                vatRate = parseFloat(vat) / 100;
            }

            const oldTotalBeforeVAT = (proforma.total ?? 0) - (proforma.vatAmount ?? 0);

            // Calculate the VAT amount
            const vatAmount = oldTotalBeforeVAT * vatRate;

            // Build proper request DTO for proforma update
            const proformaUpdate: ProformaInvoiceUpdate = {
                documentType: 'PROFORMA_INVOICE',
                status: InvoiceStatusEnum.UnpaidProforma,
                paymentMethod: paymentMethod ? paymentMethod : proforma.paymentMethod ?? undefined,
                vat: vatRate * 100
            };

            if (proforma.id != null) {
                await invoicesApi.updateProformaInvoice(proforma.id, proformaUpdate);
            }
            // Handle success if needed
        } catch (error) {
            logger.error(error);
            // Handle error if needed
        } finally {
            setLoading(false);
            setIsModalOpen(false);
        }
    }


    const modalTitle = intl.formatMessage({id: 'update_proforma_invoice'})+' '+ proforma.invoiceNumber.toString();

    const [form] = Form.useForm();

    return (
        <Modal
            className={'dentobook-modal'}
            okButtonProps={{
                className: "modal-btn"
            }}
            cancelButtonProps={{
                className: "modal-btn"
            }}
            title={modalTitle}
            width={800}
            okText={<FormattedMessage id="save"/>}
            cancelText={<FormattedMessage id="cancel"/>}
            confirmLoading={loading}
            closeIcon={<CloseOutlined className={'icon-24px'} onClick={handleCancel}/>}
            open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
            <Form layout="vertical" form={form}>
                <Row gutter={24}>
                    <Col>
                        <Form.Item
                            className={"dentobook-form-item"}
                            label={<FormattedMessage id="date"/>}
                            name="date"
                            initialValue={proforma.date ? dayjs(proforma.date) : ''}>
                            <DatePicker
                                style={!isMobileOnly ? {width: 316} : {width: '88vw'}}
                                format={"DD.MM.YYYY"}
                                value={date}
                                onChange={e => setDate(e)}
                                placeholder=""/>
                        </Form.Item>
                    </Col>
                    <Col>
                        <Form.Item
                            className={"dentobook-form-item"}
                            label={<FormattedMessage id="payment_deadline"/>}
                            name="paymentDeadline"
                            initialValue={proforma.paymentDueDate ? dayjs(proforma.paymentDueDate) : ''}>
                            <DatePicker
                                style={!isMobileOnly ? {width: 316} : {width: '88vw'}}
                                format={"DD.MM.YYYY"}
                                value={paymentDeadline}
                                onChange={e => setPaymentDeadline(e)}
                                placeholder=""/>
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    className={"dentobook-form-item input-full-width mt-16"}
                    label={<FormattedMessage id="payment_method"/>}
                    name="paymentMethod"
                    initialValue={proforma.paymentMethod ? proforma.paymentMethod : ''}
                >
                    <Select
                        value={paymentMethod}
                        onChange={e => setPaymentMethod(e)}
                        className="dentobook-input"
                        showSearch
                        suffixIcon={<CaretDownOutlined/>}
                        optionFilterProp="label"
                    >
                        <Select.Option key="cash" value="CASH"><FormattedMessage
                            id="cash_payment_type"/></Select.Option>
                        <Select.Option key="card" value="CARD"><FormattedMessage
                            id="card_payment_type"/></Select.Option>
                        <Select.Option key="bank_transfer" value="BANK_TRANSFER"><FormattedMessage
                            id="bank_transfer"/></Select.Option>
                    </Select>
                </Form.Item>
                <Form.Item
                    className={"dentobook-form-item input-full-width mt-16"}
                    label={<FormattedMessage id="vat_label"/>}
                    name="vat"
                    initialValue={proforma.vat ? proforma.vat : ''}
                    style={!isMobileOnly ? {width: 316} : {width: '88vw'}}
                >
                    <Select
                        value={vat}
                        onChange={e => setVat(e)}
                        className="dentobook-input"
                        showSearch
                        suffixIcon={<CaretDownOutlined/>}
                        optionFilterProp="label"
                    >
                        <Select.Option key="zero" value="0">0%</Select.Option>
                        <Select.Option key="nine" value="9">9%</Select.Option>
                        <Select.Option key="twenty" value="20">20%</Select.Option>
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default PatientProformaCreateModal;